import Moment from 'moment';
import { useEffect, useState } from 'react';
import { compose } from 'redux';
// materialUI
import { Box, Paper, Typography, styled } from '@mui/material';
import { makeStyles } from '@mui/styles';

// components
import { Link } from 'react-router-dom';
import { getEriskKey } from '../../../accounts/AccountService';
import { ActivityCard } from './ActivityCard';
import ClaimsCard from './ClaimsCard';
import { CowbellFactorsRangePanelContainer } from '../../../components/cowbellFactors/factorRange/CowbellFactorsRangePanelContainer';
import DashboardRiskReportCard from './DashboardRiskReportCard';
import { ERiskCard } from './quick-actions/ERiskCard';
import { CoveragePanel } from './coverage/CoveragePanel';
import { PaymentDuePanel } from './coverage/PaymentDuePanel';
import { selectPaymentDue } from './coverage/paymentDue';
import { useCoverageView } from './coverage/useCoverageView';
import { QuickActionCard } from './QuickActionCard';

// hocs / services / utils
import { withBackfill } from '../../../components/hocs/withBackfill';
import withRouter from '../../../components/hocs/withRouter';
import { useActor } from '../../../components/hooks/useActor';
import { useAiFactorScores } from '../../../components/hooks/useAiCowbellFactors';
import { AiCowbellFactorsPanel } from '../../../components/cowbellFactors/AiCowbellFactorsPanel';
import Showable from '../../../components/Showable';
import SpotlightBanner from '../../../components/spotlight/SpotlightBanner';
import { languages } from '../../../i18n/i18n.language-config';
import ResilienceServicesBanner from '../../../policyholders/resilience/components/ServiceBanner';
import { usePlatformLanguage } from '../../../utils/hooks/usePlatformLanguage';
import { useSpotlightCount } from '../../../policyholders/customer-spotlights/spotlight.hooks';
import { CARD_PADDING_REM } from '../../../components/panels/panelLayout';

/*
 * Two independent columns. Nothing on this page is coupled across them.
 *
 * The coverage card and the CRS banner were briefly one row, so that the two
 * would end level: as free-standing tiles, whether their bottoms lined up was
 * a coincidence of how the banner's copy happened to wrap, and it ran 8px
 * taller at 1440, 51px taller at 1620 and 15px shorter at 2560.
 *
 * The row bought that alignment at a price that was worse. A grid row is a
 * sibling of the columns beneath it, so its height is reserved across the full
 * width -- and a payment tile stacked above the banner made the rail cell the
 * taller one. The difference was then stranded: the factors panel could not
 * rise into it, and the page carried a band of nothing under the card in
 * exactly the state where the insured owes money (FIR-21165).
 *
 * Nothing replaces the coupling. Each tile is the height of its own content.
 *
 * A shared floor was tried -- one rem constant both tiles were held to -- and
 * it cannot work: the banner re-wraps its copy as the root font re-bases, so
 * its own height is 24.67rem at a 12px root, 26.79rem at 14px and 24.69rem at
 * 16px. No single constant equals that at every width, and the one chosen was
 * 2.1rem short at 1620 -- a pinned height pretending to be a measurement.
 *
 * So the two top tiles line up when their content happens to agree and do not
 * when it does not, which is what every other pair of tiles on this page
 * already does. The alignment was only ever visible with no payment tile in
 * the rail; with one, the banner is pushed down past the card regardless.
 */

export const CustomerDashboard = compose(
  withRouter,
  withBackfill('UiSettings.dashboard', {
    before: Moment().unix(),
    after: Moment().subtract(1, 'week').unix(),
  })
)(({ q }: any) => {
  const cardContentClasses = useCardContentStyles();
  const [eriskKey, setKey] = useState(null);
  const actor = useActor();

  const region = usePlatformLanguage();
  const isAURegion = region === languages['en-AU'];

  const aiFactorScores = useAiFactorScores(actor.account.id);
  const coverage = useCoverageView(actor.account?.id);
  const hasCoverage = coverage.view.hasAnyCoverage;
  // The card itself is conditional too -- rendering an empty Paper would leave a
  // blank white block where the tile used to be. Loading and error still show,
  // so a slow or failed read is never mistaken for an account with no coverage.
  const showCoverage = hasCoverage || coverage.isLoading || coverage.isError;
  // Only when the insured is the one billed and the premium is outstanding.
  const paymentDue = selectPaymentDue(coverage.view);
  // `hasCoverage` is false while the read is in flight, and it also decides which
  // column the movable tiles live in. Switching on it mid-load unmounted them from
  // one column and remounted them in the other, re-running their fetches and
  // flashing visible cards back to skeletons. They wait for the answer instead, so
  // each mounts exactly once, in its final place.
  const layoutSettled = !coverage.isLoading;
  const spotlightCount = useSpotlightCount({ accountId: actor.account.id });

  useEffect(() => {
    if (!actor.account?.id) {
      return;
    }

    // Only the eRiskHub key is needed here. This used to chain off getLatestPolicies() to
    // set a `product` that nothing read -- and that read now returns an array, so it was
    // both dead and wrong.
    getEriskKey()
      .then((resp: any) => setKey(resp.data))
      .catch(console.error.bind(console));
  }, [actor.account?.id]);

  return (
    <>
      <Showable show={Boolean(spotlightCount)}>
        <SpotlightBanner
          title={`You have ${spotlightCount} Spotlight(s) on this Account`}
        >
          <Typography sx={(t) => ({ color: t.palette.primary.main })}>
            Remediate the Spotlight to improve your Risk Posture &amp; Cowbell
            Factors.
            <StyledLink
              to="/customer/insights"
              style={{ marginLeft: '0.5rem' }}
            >
              View Details
            </StyledLink>
          </Typography>
        </SpotlightBanner>
      </Showable>
      <StyledDashboardLayout>
        <StyledMainColumn>
          {/*
            Coverage leads the page. It is the thing a policyholder came to see
            -- what they hold, and what still needs a decision -- and every
            other tile is context for it.

            It leads the main column rather than a row of its own. As a row it
            ended level with the head of the rail beside it, and whenever a
            payment tile made that cell the taller one the difference was
            stranded: the row is a sibling of the columns below, so the factors
            panel could not rise into it and the leftover sat on the page as a
            band of nothing. Each column stacks at its own height now, and the
            card is simply the first tile of this one.
          */}
          {showCoverage && (
            <Paper
              elevation={6}
              classes={cardContentClasses}
              data-qa="DASHBOARD_COVERAGE_CARD"
              // A column, so that whenever something above does hold this card
              // taller than its content the closing block sits on the bottom
              // edge rather than leaving the gap under the last line.
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <CoveragePanel
                view={coverage.view}
                // Not `account?.name`: `account.id` is dereferenced
                // unconditionally above, so the component has already thrown by
                // here if there is no account. The optional chain only looked
                // like a guard.
                companyName={actor.account.name}
                isLoading={coverage.isLoading}
                isError={coverage.isError}
              />
            </Paper>
          )}
          {/*
            CRS leads the main column only while there is no coverage tile above
            it. Once coverage renders it moves to the rail, so the main column
            stays a single narrative: what you hold, then the factors behind it.

            The risk report does not pair with it here. It renders in the rail
            in both cases -- see the rail below.
          */}
          {layoutSettled && !hasCoverage && <ResilienceServicesBanner />}
          <Paper elevation={6} classes={cardContentClasses}>
            <CowbellFactorsRangePanelContainer
              accountId={actor.account.id}
              companyName={actor.account?.name}
              before={q?.before}
              after={q?.after}
            />
            <AiCowbellFactorsPanel
              data={aiFactorScores.data}
              isLoading={aiFactorScores.isLoading}
            />
          </Paper>
          {/*
            eRiskHub and support close the main column once coverage leads it.
            They are things to go and do rather than things to read, so they sit
            after the factors instead of competing with them in the rail.
          */}
          {layoutSettled && hasCoverage && (
            <StyledPairRow $single={isAURegion}>
              {!isAURegion && (
                <Paper elevation={6} classes={cardContentClasses}>
                  <ERiskCard eKey={eriskKey} />
                </Paper>
              )}
              <Paper elevation={6} classes={cardContentClasses}>
                <QuickActionCard />
              </Paper>
            </StyledPairRow>
          )}
        </StyledMainColumn>

        {/*
          Rail order: payment due, CRS, claims, risk report, eRiskHub,
          activity, support. Money leads it -- an outstanding premium is the
          only thing on this page with a consequence attached.
          Support is deliberately last: it is where a reader ends up when
          nothing above it answered them, so it cannot sit above the things
          that might. The risk report renders here whether or not coverage does;
          it used to move into the main column when coverage was absent.
        */}
        <StyledRailColumn data-qa="DASHBOARD_RAIL">
          {/*
            Money first. An outstanding premium is the only thing on this page
            with a consequence attached, so it heads the rail rather than being
            demoted below the promo.
          */}
          {showCoverage && paymentDue && (
            <Paper
              elevation={6}
              classes={cardContentClasses}
              data-qa="DASHBOARD_PAYMENT_DUE"
            >
              <PaymentDuePanel payment={paymentDue} />
            </Paper>
          )}
          {/*
            No Paper wrapper. The banner is already its own full-bleed card, so
            the padded white Paper the other tiles use framed it in white.
          */}
          {showCoverage && layoutSettled && hasCoverage && (
            <ResilienceServicesBanner />
          )}
          <Paper elevation={6} classes={cardContentClasses}>
            <ClaimsCard />
          </Paper>
          {/* Also its own card -- see the CRS note above. */}
          {layoutSettled && (
            <DashboardRiskReportCard accountId={actor.account.id} />
          )}
          {/* eRiskHub is not offered in AU, and moves to the main column when
              coverage renders -- see the services row there. */}
          {layoutSettled && !hasCoverage && !isAURegion && (
            <Paper elevation={6} classes={cardContentClasses}>
              <ERiskCard eKey={eriskKey} />
            </Paper>
          )}
          <Paper elevation={6} classes={cardContentClasses}>
            <ActivityCard />
          </Paper>
          {/* Support is last wherever it sits: it is where a reader ends up when
              nothing above answered them. */}
          {layoutSettled && !hasCoverage && (
            <Paper elevation={6} classes={cardContentClasses}>
              <QuickActionCard />
            </Paper>
          )}
        </StyledRailColumn>
      </StyledDashboardLayout>
    </>
  );
});

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.text12,
}));

/**
 * The rail's share of the page width.
 */
const RAIL_BASIS = '28%';

// From the shared constant, not a hand-written value: the full-bleed bands
// inside a tile cancel exactly this padding, and when the two were written
// independently they drifted and the bands overhung the card edge.
const useCardContentStyles = makeStyles(() => ({
  root: {
    padding: CARD_PADDING_REM,
    height: '100%',
    // The radius every tile shares, rather than MUI's fixed 5px. A tile that
    // has something drawn flush to its own edges -- the coverage tile's quote
    // frame -- has to be able to match its corners, and it cannot match a
    // value that stops scaling when the root font re-bases.
    borderRadius: 'var(--tile-radius)',
  },
}));

/**
 * Two independent columns rather than one grid.
 *
 * As a three-column grid every tile in a row was forced to the height of the
 * tallest -- the stacked Support, eRiskHub, claims and activity cards. That
 * left the tiles beside them either padded out with empty space or, when sized
 * to their content, ending early with a visible gap underneath. Neither is
 * fixable by spacing, because the coupling is the layout itself.
 *
 * Each column now stacks at its own natural height and nothing crosses between
 * them, so the rail can be as long as it likes without dictating anything.
 * `flex-start` is what keeps the two columns from stretching each other.
 */
const StyledDashboardLayout = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--tile-gutter)',

  // Below md the two columns stack. Without this the row never wraps and the
  // rail keeps its `flex-shrink: 0`, so a full-width rail squeezed the main
  // column -- banner, risk report and factors -- to zero width.
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',

    // Stacked, two columns play out one after the other: the whole main column,
    // then the whole rail. That buried an outstanding premium 1,560px down the
    // page, below the factors panel -- the one tile here with a consequence
    // attached, and the reason a policyholder opened the page at all.
    //
    // Each column drops to `display: contents` at this width (see both of
    // them below), so every tile becomes a child of this single stack and the
    // three with a reading order worth keeping can claim it. Everything else
    // falls where the markup puts it.
    //
    // The columns' own `gap` goes with their boxes; this one replaces it, which
    // is why the stacked gutter stays the same size.
    '& [data-qa="DASHBOARD_COVERAGE_CARD"]': {
      order: -2,
    },
    // Equal order, so these two fall back to markup order -- payment, then the
    // promo it must never sit beneath.
    '& [data-qa="DASHBOARD_PAYMENT_DUE"], & [data-testid="services-banner"]': {
      order: -1,
    },
  },
}));

const StyledMainColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--tile-gutter)',
  // `0` basis with `minWidth: 0` so the column shares the row by ratio and its
  // children can still shrink below their content width.
  flex: '1 1 0',
  minWidth: 0,

  // Stacked, this column is not a column at all -- it is the first half of one
  // list. Dissolving the box lets the layout above order the tiles across both
  // halves; set on the column itself rather than from the parent, which ties
  // this rule on specificity and loses to it on sheet order.
  [theme.breakpoints.down('md')]: {
    display: 'contents',
  },
}));

const StyledRailColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--tile-gutter)',
  flex: `0 0 ${RAIL_BASIS}`,
  minWidth: 0,

  // Stacked, this column is dissolved for the same reason as the main one --
  // see there. That also settles what the no-shrink basis used to break here:
  // with no box, there is nothing left to collapse the column beside it.
  [theme.breakpoints.down('md')]: {
    display: 'contents',
  },
}));

/**
 * The two intro tiles share a row and match each other, which is a deliberate
 * pairing rather than the accidental coupling above: they sit side by side and
 * read as one band.
 */
const StyledPairRow = styled('div', {
  shouldForwardProp: (prop) => prop !== '$single',
})<{ $single?: boolean }>(({ theme, $single }) => ({
  display: 'grid',
  gridTemplateColumns: $single ? '1fr' : '1fr 1fr',
  alignItems: 'stretch',
  gap: 'var(--tile-gutter)',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr',
  },
}));
